import { Breakpoints, styled } from '@volkswagen-onehub/components-core';
import { ListProps } from './types';

export const TextContentLayout = styled.div``;

export const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const ContentBlock = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-width: 0;
`;

export const Row = styled.div<{
  $reverse?: boolean;
  $normalize?: boolean;
  $centerTextVertically?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;

  ${({ $normalize }) =>
    $normalize &&
    `
    div > ol:first-child,
    div > ul:first-child
 {
      margin-top: 0px !important;
    }
  `}

  @media (min-width: ${Breakpoints.b560}px) {
    flex-direction: ${({ $reverse }) => ($reverse ? 'row-reverse' : 'row')};
    align-items: stretch;
    gap: ${({ $centerTextVertically }) =>
      $centerTextVertically ? '32px' : '67px'};
  }

  ${({ $gap }) =>
    $gap &&
    `
    @media (min-width: ${Breakpoints.b560}px) {
      gap: ${$gap};
    }
  `}
`;

export const Column = styled.div<{
  $normalize?: boolean;
  $centerVertically?: boolean;
}>`
  width: 100%;
  max-width: 100%;
  min-width: 0;

  ${({ $normalize }) =>
    $normalize &&
    `
    ol:first-child,
    ul:first-child
 {
      margin-top: 0px !important;
    }
  `}

  @media (min-width: ${Breakpoints.b560}px) {
    flex: 1;
    ${({ $centerVertically }) =>
      $centerVertically
        ? `
      display: flex;
      flex-direction: column;
      justify-content: center;
      margin-bottom: 16px;
    `
        : ''}
  }
`;

export const MediaColumn = styled.div<{
  $isTwoColumn?: boolean;
  $centerVertically?: boolean;
  $maxWidth?: number;
}>`
  width: 100%;

  /*
    width: 100vw;
    max-width: 100vw;
    margin-left: calc((100vw - 100%) / -2);
    margin-right: calc((100vw - 100%) / -2);
    margin-top: 1rem;
    margin-bottom: 1rem;
  */

  @media (min-width: ${Breakpoints.b560}px) {
    ${({ $isTwoColumn, $maxWidth }) =>
      !$isTwoColumn
        ? `
      width: 100%;
      max-width: 100%;
      margin-left: 0;
      margin-right: 0;
    `
        : `
      width: 45%;
      max-width: 500px;
      margin-left: 0;
      margin-right: 0;
      margin-top: 0;
      flex: 0 0 45%;
      ${$maxWidth && $maxWidth > 0 ? `max-width: ${$maxWidth}px;` : ''}
    `}

    ${({ $centerVertically }) =>
      $centerVertically ? `align-self: center;` : ''}
  }
`;

// Flow layout - text flows naturally around floated media
export const FlowRow = styled.div`
  position: relative;
  overflow: visible; /* Allow media to break out on mobile */

  @media (min-width: ${Breakpoints.b560}px) {
    overflow: auto; /* Ensures container wraps floated content on desktop */
  }
`;

export const FlowMediaColumn = styled.div<{
  $isTwoColumn?: boolean;
  $reverse?: boolean;
  $maxWidth?: number;
}>`
  width: 100%;
  margin-bottom: 1rem;

  /*
    float: none;
    width: 100vw;
    max-width: 100vw;
    margin-left: calc((100vw - 100%) / -2);
    margin-right: calc((100vw - 100%) / -2);
    margin-top: 0;
    margin-bottom: 1rem;
  */

  @media (min-width: ${Breakpoints.b560}px) {
    float: ${({ $reverse }) => ($reverse ? 'right' : 'left')};
    width: 45%;
    max-width: 500px;
    z-index: 1;
    position: relative;
    margin: ${({ $reverse }) =>
      $reverse ? '0 0 1.5rem 67px' : '0 67px 1.5rem 0'};
    ${({ $isTwoColumn, $maxWidth }) =>
      $isTwoColumn &&
      `
     ${$maxWidth && $maxWidth > 0 ? `max-width: ${$maxWidth}px;` : ''}
      margin-top: 0.25rem;
    `}
  }
`;

export const FlowColumn = styled(Column)`
  /* Text flows naturally around floated media */

  /* Clear float after content */
  &::after {
    content: '';
    display: table;
    clear: both;
  }
`;

export const StyledIconImage = styled.img<{
  $isRetina: boolean;
  $isLandscape: boolean | null;
}>`
  width: auto;
  max-height: ${({
    $isRetina,
    $isLandscape,
  }: {
    $isRetina: boolean;
    $isLandscape: boolean | null;
  }) => ($isLandscape ? '18px' : $isRetina ? '24px' : '24px')};
  vertical-align: ${({ $isRetina, $isLandscape }) =>
    $isLandscape ? '-2px' : $isRetina ? '-4px' : '-4px'};
`;

// Modified UL for PDF export
export const StyledUL = styled.ul<ListProps>`
  margin: 12px 0;
  padding-left: 0;
  list-style: none;

  & > li {
    position: relative;
    padding-left: 0.75em;
    margin-bottom: 12px;
    line-height: 1.5;
  }

  & > li:not([data-icon-marker='true'])::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.95em;
    width: 5.5px;
    height: 6px;
    background-color: currentColor;
    border-radius: 50%;
    transform: translateY(-50%);
  }

  & ol,
  & ul {
    padding-left: 1em;
    padding-right: 0;
  }
`;

// Normal UL
/*export const StyledUL = styled.ul<ListProps>`
  margin: 1em 0;
  padding-left: 1em;
  padding-right: 0;
  list-style: disc;

  margin-top: 12px;
  margin-bottom: 12px;
  & > li {
    margin-bottom: 12px;
    vertical-align: baseline;
    line-height: 1.5;
    list-style-position: outside;
  }

  ul,
  ol {
    padding-left: 1.5em;
    padding-right: 0;
  }
`;*/

// Modified OL for PDF export
export const StyledOL = styled.ol<ListProps>`
  margin: 12px 0;
  padding-left: 0;
  list-style: none;
  counter-reset: item ${({ $start }) => ($start && $start > 1 ? $start - 1 : 0)};

  & > li {
    position: relative;
    padding-left: 2.75ch; // 1.5em;
    margin-bottom: 12px;
    line-height: 1.5;
    counter-increment: item;
    margin-left: -0.4rem;
  }

  & > li:not([data-icon-marker='true'])::before {
    content: counter(item) '.';
    position: absolute;
    left: 0;
    top: 0.1em;
    font-weight: bold;
    color: ${({ theme }) => theme.colors.content.active};
    width: 2.25ch;
    text-align: right;
  }

  & ol,
  & ul {
    padding-left: 1em;
    padding-right: 0;
  }
`;

// Normal OL
/*export const StyledOL = styled.ol<ListProps>`
  margin: 1em 0;
  padding-left: 1em;
  padding-right: 0;
  list-style: decimal;

  margin-top: 12px;
  margin-bottom: 12px;
  & > li {
    margin-bottom: 12px;
    vertical-align: baseline;
    line-height: 1.5;
    list-style-position: outside;
  }

  & > li::marker {
    color: ${({ theme }) => theme.colors.content.active};
    font-weight: bold;
    vertical-align: baseline;
  }

  ul,
  ol {
    padding-left: 1.5em;
    padding-right: 0;
  }
`;*/

export const StyledLI = styled.li<{ $hasIconMarker?: boolean }>`
  ${({ $hasIconMarker }) =>
    $hasIconMarker &&
    `
    list-style: none;
    position: relative;
    padding-left: 1.5em;
    overflow: visible;
    min-height: 1.5em;
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0.2em;
    }
  `}
`;

export const LIIconMarker = styled.span`
  position: absolute;
  left: 0;
  top: 0.25em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 1.15rem;
  width: 3rem;
  overflow: visible;

  svg {
    display: block;
    width: auto;
    height: auto;
    max-width: 3rem; /* Cap icon width to prevent clipping */
    flex-shrink: 0;
    object-fit: contain;
  }

  img {
    display: block;
    width: auto;
    height: auto;
    max-width: 3rem; /* Cap icon width to prevent clipping (40px) */
    flex-shrink: 0;
    object-fit: contain;
  }
`;

export const LIContent = styled.span`
  display: block;
  margin-left: 2.75rem; /* Adjusted margin to account for icon container width */
  min-height: 1rem; /* Ensure minimum height */
`;
